import { useEffect, useState, useCallback } from "react";
import Box from '@mui/material/Box'
import { useNavigate, useParams } from "react-router-dom";
import DataTable, { Column } from "../components/dataTable";
import { useSnackbar } from "../../Components/Snackbar/SnackBarContext";
import { debounce } from "lodash";
import { callAPI, Method } from "../../commonFunctions/CommonFunctions";
import { Header } from "../../Components/Header/Header";

const FilteredRepayments = () => {
    const [loading, setLoading] = useState<boolean>(true);
    const [repayments, setRepayments] = useState([]);
    const [search, setSearch] = useState<string>("");
    const [page, setPage] = useState<number>(1);
    const [totalPages, setTotalPages] = useState<number>(1);
    const [rowsPerPage, setRowsPerPage] = useState<number>(5)
    const [sortOption, setSortOption] = useState("created_desc");
    const [filterOption, setFilterOption] = useState<string>("today");
    const [fromDate, setFromDate] = useState<string | null>(null);
    const [toDate, setToDate] = useState<string | null>(null);

    const { loanId } = useParams();
    const { showMessage } = useSnackbar();
    const navigate  = useNavigate();

    let dateFilter = {};
    if (filterOption === "today") {
        dateFilter = { createdToday: "true" };
    } else if (filterOption === "thisMonth") {
        const now = new Date();
        const firstDay = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
        const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999).toISOString();
        dateFilter = { dateFrom: firstDay, dateTo: lastDay };
    }

    const fetchData = useCallback(
        debounce(
            async (
                pageVal: number,
                searchVal: string,
                limitVal: number,
                sortVal: string,
                extraParams: Record<string, string> = {},
            ) => {
                setLoading(true);
                try {
                    const query = new URLSearchParams({
                        page: String(pageVal),
                        search: searchVal,
                        limit: String(limitVal),
                        ...(sortVal && { sort: sortVal }),
                        ...extraParams,
                    });

                    const res = await callAPI(
                        `repayments/getPaginated?${query.toString()}`,
                        Method.GET
                    );

                    if (res.success) {
                        setRepayments(res.repayments || []);
                        setTotalPages(res.totalPages || 0);
                    }
                } catch (error: any) {
                    console.error("Failed to fetch data:", error);
                    if (error.message === 'GlobalHandledError') return;
                    showMessage(
                        error.response?.data.message || "An unexpected error occurred",
                        "error"
                    );
                } finally {
                    setLoading(false);
                }
            },
            400
        ),
        [setLoading, setRepayments, setTotalPages]
    );


    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearch(e.target.value);
        setPage(1);
    };

    useEffect(() => {
        const extraParams: Record<string, string> = {};

        // Optional loanId filter
        if (loanId) {
            extraParams.loanId = loanId;
        }

        // Date-based filters
        if (filterOption === "today") {
            extraParams.createdToday = "true";
        } else if (filterOption === "thisMonth") {
            const now = new Date();
            const firstDay = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
            const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999).toISOString();
            extraParams.dateFrom = firstDay;
            extraParams.dateTo = lastDay;
        } else if (filterOption === "custom" && fromDate && toDate) {
            extraParams.dateFrom = new Date(fromDate).toISOString();
            extraParams.dateTo = new Date(toDate + "T23:59:59.999Z").toISOString();
        }

        fetchData(page, search, rowsPerPage, sortOption, extraParams);
    }, [page, search, rowsPerPage, sortOption, loanId, filterOption, fromDate, toDate]);


    const handleFilterChange = (value: string) => {
        
        setFilterOption(value);
        // Reset custom dates if switching filters
        if (value !== "custom") {
            setFromDate(null);
            setToDate(null);
        }
    }

    const handleSortChange = (value: string) => {
        setSortOption(value);
        setPage(1);
    }

    const columns: Column[] = [
        { field: "receiptNumber", label: "Receipt" },
        // { field: "loan.applicantName", label: "Applicant Name" },
        { field: "loanData.enterpriseName", label: "Enterprise Name" },
        // { field: "interestComponent", label: "Interest Rate", type: "currency" },
        { field: "amount", label: "Paid Amount", type: "currency" },
        {
            field: "updatedAt",
            label: "Last Updated",
            sortable: true,
            type: "date",
        },
    ];

    const actions = [{ label: "View Details", handler: (row: any) => navigate(`/loan/repayment/details/${row?._id}`) }]

    return (
        <Box>
            <Header
                type="new"
                title="Repayments"
                id={"repaymentHeader"}
            />
            <DataTable
                columns={columns}
                data={repayments}
                loading={loading}
                onPageChange={setPage}
                filters={{
                    options: [
                        { value: 'all', label: 'All' },
                        { value: 'today', label: 'Today' },
                        { value: 'thisMonth', label: 'This Month' },
                        { value: 'custom', label: 'Custom Date' }
                    ],
                    defaultValue: 'today',
                    value: filterOption,
                    onChange: handleFilterChange,
                    ...(filterOption === 'custom' && {
                        customDateRange: {
                            fromDate: fromDate,
                            toDate: toDate,
                            onFromDateChange: setFromDate,
                            onToDateChange: setToDate
                        }
                    })
                }}
                sort={{
                    options: [
                        { value: 'name_asc', label: 'Name (A → Z)' },
                        { value: 'name_desc', label: 'Name (Z → A)' },
                        { value: 'created_desc', label: 'Newest' },
                        { value: 'created_asc', label: 'Oldest' }
                    ],
                    defaultValue: 'created_desc',
                    value: sortOption,
                    onChange: handleSortChange
                }}
                search={{
                    fieldName: 'searchQuery',
                    onChange: handleSearchChange,
                    placeholder: 'Search receipt...'
                }}
                page={page}
                rowsPerPage={rowsPerPage}
                setRowsPerPage={setRowsPerPage}
                totalPages={totalPages}
                actions={actions}
                exportToExcel
            />
        </Box>
    );
};

export default FilteredRepayments;